import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import { companyInfo, navLinks, services } from '../../data/mock';
import logoImg from '../../assets/logo.png';

export const Footer = () => {
  return (
    <footer className="bg-[#0a0a0f] border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Company Info */}
          <div className="space-y-5">
            <div className="flex items-center">
              <img
                src={logoImg}
                alt={companyInfo.name}
                className="h-20 w-auto drop-shadow-[0_0_15px_rgba(59,130,246,0.5)]"
              />
            </div>
            <p className="text-slate-500 text-sm leading-relaxed" style={{ maxWidth: '260px' }}>
              Operational support for growing businesses. Expert teams, proven processes.
            </p>
            {/* Social Links */}
            <div className="flex items-center gap-3 pt-2">
              {/* Instagram */}
              <a href="https://www.instagram.com/viso_connect/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-white/5 hover:bg-gradient-to-br hover:from-purple-500 hover:to-pink-500 rounded-lg flex items-center justify-center transition-all duration-300">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-400 group-hover:text-white">
                  <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                  <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
                </svg>
              </a>
              {/* LinkedIn */}
              <a href="https://linkedin.com/company/visoconnect/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-white/5 hover:bg-gradient-to-br hover:from-blue-500 hover:to-blue-700 rounded-lg flex items-center justify-center transition-all duration-300">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-400 group-hover:text-white">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                  <rect width="4" height="12" x="2" y="9" />
                  <circle cx="4" cy="4" r="2" />
                </svg>
              </a>
              {/* X (Twitter) */}
              <a href="https://x.com/visoconnect?s=11" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-white/5 hover:bg-gradient-to-br hover:from-slate-600 hover:to-slate-900 rounded-lg flex items-center justify-center transition-all duration-300">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-slate-400 group-hover:text-white">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-semibold text-white text-sm mb-5">Company</h4>
            <nav className="space-y-3">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="block text-slate-500 hover:text-white transition-colors duration-200 text-sm"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold text-white text-sm mb-5">Services</h4>
            <nav className="space-y-3">
              {services.map((service) => (
                <Link
                  key={service.id}
                  to={`/services#${service.id}`}
                  className="block text-slate-500 hover:text-white transition-colors duration-200 text-sm"
                >
                  {service.title}
                </Link>
              ))}
            </nav>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-white text-sm mb-5">Contact</h4>
            <div className="space-y-4">
              <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-3 text-slate-500 hover:text-white transition-colors text-sm">
                <Mail size={16} className="text-indigo-400" />
                {companyInfo.email}
              </a>
              <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-3 text-slate-500 hover:text-white transition-colors text-sm">
                <Phone size={16} className="text-indigo-400" />
                {companyInfo.phone}
              </a>
              {companyInfo.phone2 && (
                <a href={`tel:${companyInfo.phone2}`} className="flex items-center gap-3 text-slate-500 hover:text-white transition-colors text-sm">
                  <Phone size={16} className="text-indigo-400" />
                  {companyInfo.phone2}
                </a>
              )}
              <div className="flex items-start gap-3 text-slate-500 text-sm">
                <MapPin size={16} className="text-indigo-400 flex-shrink-0 mt-0.5" />
                <span>{companyInfo.address.street}, {companyInfo.address.city}, {companyInfo.address.postcode}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/5 mt-14 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-600 text-sm">
              © {new Date().getFullYear()} {companyInfo.name}. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/privacy" className="text-slate-600 hover:text-white text-sm transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-slate-600 hover:text-white text-sm transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
